import getConfig from 'next/config';

import { logger } from '@klokhuis/core/Logger';

import { ApiClientBase } from './ApiClientBase';
import {
  AuthApi,
  Configuration,
  DebugApi,
  FavoriteApi,
  GisApi,
  ListingApi,
  MediaApi,
  MessagesApi,
  Middleware,
  PaymentApi,
  SearchApi,
  SellersApi,
} from './Klokhuis';
import { KlokhuisError } from './KlokhuisErrors';

const { publicRuntimeConfig } = getConfig();

const isServer = typeof global.window === 'undefined';

export class ApiClient extends ApiClientBase {
  auth: AuthApi;

  favorite: FavoriteApi;

  listing: ListingApi;

  payment: PaymentApi;

  search: SearchApi;

  gis: GisApi;

  debug: DebugApi;

  sellers: SellersApi;

  media: MediaApi;

  messages: MessagesApi;

  constructor() {
    super({
      klokhuisWebOrigin: publicRuntimeConfig.ORIGIN,
      klokhuisApiOrigin: publicRuntimeConfig.KLOKHUIS_API_ORIGIN + '/api',
    });

    const configuration = new Configuration({
      basePath: this.klokhuisApiOrigin,
      credentials: 'include',
    });

    // @todo revisit response and error handling
    const normalizationMiddleware: Middleware = {
      pre: async context => {
        logger.info({
          message: `ApiClient: ${context.init.method} ${context.url} : requesting`,
          method: context.init.method,
          url: context.url,
        });

        const extraHeaders = {
          ...(isServer ? this.headers : undefined),
        };

        const authorizationHeader = this.authorizationHeaderGetter?.();
        if (authorizationHeader) {
          extraHeaders.Authorization = authorizationHeader;
        }

        return {
          url: context.url,
          init: {
            ...context.init,
            headers: {
              ...context.init.headers,
              ...extraHeaders,
            },
          },
        };
      },
      post: async context => {
        logger.info({
          message: `ApiClient: ${context.init.method} ${context.url} : responded with status ${context.response.status}`,
          method: context.init.method,
          url: context.url,
          status: context.response.status,
        });

        await this.handleResponse(
          Promise.resolve(context.response.clone()),
          KlokhuisError,
        );

        return context.response;
      },
      onError: async context => {
        logger.error(
          `ApiClient: ${context.init.method} ${context.url} : ${context.error}`,
        );

        if (context.response) {
          const { response } = await this.handleResponse(
            Promise.resolve(context.response.clone()),
            KlokhuisError,
          );

          return response;
        }

        return undefined;
      },
    };

    this.auth = new AuthApi(configuration).withMiddleware(
      normalizationMiddleware,
    );

    this.favorite = new FavoriteApi(configuration).withMiddleware(
      normalizationMiddleware,
    );

    this.gis = new GisApi(configuration).withMiddleware(
      normalizationMiddleware,
    );
    this.listing = new ListingApi(configuration).withMiddleware(
      normalizationMiddleware,
    );

    this.search = new SearchApi(configuration).withMiddleware(
      normalizationMiddleware,
    );

    this.payment = new PaymentApi(configuration).withMiddleware(
      normalizationMiddleware,
    );

    this.sellers = new SellersApi(configuration).withMiddleware(
      normalizationMiddleware,
    );

    this.media = new MediaApi(configuration).withMiddleware(
      normalizationMiddleware,
    );

    this.messages = new MessagesApi(configuration).withMiddleware(
      normalizationMiddleware,
    );

    this.debug = new DebugApi(configuration).withMiddleware(
      normalizationMiddleware,
    );
  }
}
